<template>
  <ui-input
    v-model="pValue"
    class="ui-input-time"
    return-masked-value
    :mask="mask"
    :hide-details="1"
    @blur="handleBlur"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import moment from 'moment';

export interface Time {
  hours: number;
  minutes: number;
}

const TIME_REGEX = /^(\d{1,2}):(\d{1,2})$/;

const props = withDefaults(
  defineProps<{
    value?: string;
    modelValue?: string;
    default?: string;
    mask?: string;
    min?: string;
    max?: string;
    minutesAttractor?: number[];
  }>(),
  {
    default: '00:00',
    mask: '##:##',
  },
);

const emit = defineEmits<{
  input: [value: string];
  'update:modelValue': [value: string];
}>();

const pValue = ref('');

const effectiveValue = computed(() =>
  props.modelValue !== undefined && props.modelValue !== null ? props.modelValue : props.value,
);

const emitValue = (value: string) => {
  emit('input', value);
  emit('update:modelValue', value);
};

const getTime = (value: string): Time | null => {
  const match = value.match(TIME_REGEX);
  if (!match) {
    return null;
  }

  let hours = +match[1];
  if (hours > 23) {
    hours = 23;
  } else if (hours < 0) {
    hours = 0;
  }

  let minutes = +match[2];

  if (props.minutesAttractor) {
    let minValue = Number.MAX_SAFE_INTEGER;
    let attractor = minutes;

    for (const newAttractor of props.minutesAttractor) {
      const newMinValue = Math.abs(minutes - newAttractor);
      if (newMinValue < minValue) {
        attractor = newAttractor;
        minValue = newMinValue;
      }
    }

    minutes = attractor;
  }

  if (minutes > 59) {
    minutes = 59;
  } else if (minutes < 0) {
    minutes = 0;
  }

  return {
    hours,
    minutes,
  };
};

const isMinMaxConstraint = (value: string) => {
  const minCond = props.min && value < props.min;
  const maxCond = props.max && value > props.max;
  return minCond || maxCond;
};

const isValueMinMaxConstraint = computed(() =>
  isMinMaxConstraint(effectiveValue.value ?? props.default),
);

watch(
  [effectiveValue, () => props.default],
  () => {
    const value = effectiveValue.value ?? props.default;
    if (TIME_REGEX.test(value)) {
      pValue.value = value;
    } else {
      emitValue(props.default);
    }
  },
  { immediate: true },
);

watch(isValueMinMaxConstraint, (value) => {
  if (value) {
    emitValue(props.default);
  }
});

const handleBlur = () => {
  processValue();
};

const processValue = () => {
  const time = getTime(pValue.value);

  if (time) {
    const result = moment(`${time.hours}:${time.minutes}`, 'HH:mm').format('HH:mm');
    pValue.value = result;
    emitValue(pValue.value);
    return;
  }

  pValue.value = effectiveValue.value ?? props.default;
};
</script>

<style lang="scss">
.ui-input-time {
  width: 50px;
}
</style>
